'use client';

import { Button, buttonVariants } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useSignIn } from '@/hooks/useSignIn';
import { errorHandler } from '@/lib/errorHandler';
import { cn } from '@/lib/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff } from 'lucide-react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

const formSchema = z.object({
  email: z.string().email({
    message: 'Please enter a valid email address',
  }),
  password: z.string().min(1, {
    message: 'Password is required',
  }),
  rememberMe: z.boolean().optional(),
});

interface SignInFormProps {
  onVerificationRequired?: (email: string) => void;
}

export const SignInForm = ({ onVerificationRequired }: SignInFormProps) => {
  const [showPassword, setShowPassword] = useState(false);

  const router = useRouter();
  const searchParams = useSearchParams();
  const redirectTo = searchParams.get('redirectTo') || '/home';
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
  });

  const { mutate: signIn, isPending: isLoading } = useSignIn();

  function onSubmit(values: z.infer<typeof formSchema>) {
    signIn(
      {
        email: values.email,
        password: values.password,
        rememberMe: values.rememberMe,
      },
      {
        onSuccess: (res) => {
          if (!res.data.userName) {
            // If the userName is not set, redirect to profile setup
            toast.success('Login successful. Please complete your profile.');
            router.push('/profile-setup');
          } else {
            // If the userName is set, redirect to dashboard
            toast.success('Login successful.');
            router.push(redirectTo);
          }
        },
        onError: (error) => {
          const errorMessage =
            error.response?.data?.error?.message || 'An error occurred';

          // Check if user is not verified
          if (
            errorMessage === 'User is not verified' &&
            onVerificationRequired
          ) {
            onVerificationRequired(values.email);
            return;
          }

          toast.error(errorHandler(error));
          if (error.response?.data?.statusCode === 403) {
            router.push('/blocked-user');
          }
        },
      }
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input placeholder="Enter your email" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="relative">
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      placeholder="Enter your password"
                      type={showPassword ? 'text' : 'password'}
                      {...field}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={
                        showPassword ? 'Hide password' : 'Show password'
                      }
                    >
                      {showPassword ? (
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      )}
                    </Button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Link
            className={cn(
              buttonVariants({ variant: 'link' }),
              'absolute -right-2 -top-3   hover:bg-transparent'
            )}
            href="/forgot-password"
          >
            Forgot password?
          </Link>
        </div>

        <FormField
          control={form.control}
          name="rememberMe"
          render={({ field }) => (
            <FormItem className="flex items-center space-x-1">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>Remember me</FormLabel>
              </div>
            </FormItem>
          )}
        />

        <Button
          isLoading={isLoading}
          className="w-full font-bold uppercase"
          type="submit"
        >
          LOG IN
        </Button>
      </form>
    </Form>
  );
};
